import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/700.css';
import './styles/styles.css';

import { AutoLogoutProvider, SignInPage } from '@agilelab/plugin-wb-auth';
import {
  CatalogEntityPage,
  CatalogGraphRenderNode,
  CatalogIndexPage,
  AdministratorCatalogIndexPage,
  catalogPlugin,
  MyProjectsPage,
  ReleaseDetailPage,
} from '@agilelab/plugin-wb-builder-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@agilelab/plugin-wb-catalog-import';
import { eventsApiRef } from '@agilelab/plugin-wb-events';
import { userSignedInTopic } from '@agilelab/plugin-wb-events-common';
import {
  WbExtensionManagerPage,
  wbExtensionManagerPlugin,
} from '@agilelab/plugin-wb-extension-manager';
import {
  GovernancePage,
  governancePlugin,
} from '@agilelab/plugin-wb-governance';
import {
  MarketplacePage,
  marketplacePlugin,
} from '@agilelab/plugin-wb-marketplace';
import {
  NotificationPage,
  NotificationProvider,
} from '@agilelab/plugin-wb-notification';
import {
  CustomAlertDisplay,
  EnabledFeaturesProvider,
  EnvironmentsContextProvider,
  LightAgileTheme,
  MeshApolloProvider,
  useCustomTheme,
  WbHomeRedirect,
  WbRequirePermission,
} from '@agilelab/plugin-wb-platform';
import {
  PlatformSettingsPage,
  platformSettingsPlugin,
} from '@agilelab/plugin-wb-platform-settings';
import { WITBOOST_RELATION_PAIRS } from '@agilelab/plugin-wb-practice-shaper-common';
import { RbacPanel } from '@agilelab/plugin-wb-rbac';
import {
  ArrayTableTemplateLayout,
  BlueprintsPage,
  HorizontalTemplateLayout,
  ScaffolderLayouts,
  ScaffolderPage,
  scaffolderPlugin,
  TableRowTemplateLayout,
  TemplatesPage,
  WittyTableRowTemplateLayout,
  WittyTableTemplateLayout,
} from '@agilelab/plugin-wb-scaffolder';
import { WbSystemPrototypePage } from '@agilelab/plugin-wb-system-prototype';
import {
  DocumentsViewerTab,
  UserSettingsPage,
  UserSettingsTab,
} from '@agilelab/plugin-wb-user-settings';
import { createApp } from '@backstage/app-defaults';
import {
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { OAuthRequestDialog } from '@backstage/core-components';
import { IdentityApi, useApi } from '@backstage/core-plugin-api';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogGraphPage,
  catalogGraphPlugin,
} from '@backstage/plugin-catalog-graph';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  techdocsPlugin,
  TechDocsReaderPage,
  TechDocsReaderPageContent,
} from '@agilelab/plugin-wb-techdocs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Route } from 'react-router';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { DocumentsPopup } from './components/documents/DocumentsPopup';
import { Root } from './components/Root';
import { UserConfigSettings } from './components/settings/UserConfigSettings';
import { UserHeadersSettings } from './components/settings/UserHeadersSettings/UserHeadersSettings';
import { UserAccessTokenSettings } from './components/settings/UserAccessTokenSettings';
import { CommercialGate } from './components/common/CommercialGate';
import {
  getUserConfigsRequiredModules,
  getUserHeadersRequiredModules,
  getUserAccessTokensRequiredModules,
  getAdvancedRequiredModules,
} from './components/settings/requiredModules';
import { accessControlPlugin } from '@agilelab/plugin-wb-access-control';
import * as plugins from './plugins';

const queryClient = new QueryClient({
  // Disable refetch on window focus globally to avoid too many requests to backend.
  // It can be re-enabled on specific queries if needed
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const scaffolderLayouts = () => {
  return (
    <ScaffolderLayouts>
      <TableRowTemplateLayout />
      <WittyTableRowTemplateLayout />
      <ArrayTableTemplateLayout />
      <WittyTableTemplateLayout />
      <HorizontalTemplateLayout />
    </ScaffolderLayouts>
  );
};

const SignInComponent: any = (props: any) => {
  const eventsApi = useApi(eventsApiRef);
  // The CustomAlertDisplay component is used to display alerts in the sign in page (e.g. when the user is not authorized).
  // You can see that inside the AppRouter, another CustomAlertDisplay component is used to display alerts in the main app.
  // This is because the sign in page does not have a Router, so links inside the toasts will fail: for this reason we need to have two separate ones.
  // N.B.: the Backstage's AppRouter class will destroy the CustomAlertDisplay component when the login completes, so the two components will not conflict.
  return (
    <>
      <CustomAlertDisplay />
      <SignInPage
        {...props}
        auto
        title="Select a sign-in method"
        align="center"
        onSignInSuccess={async (identityApi: IdentityApi) => {
          const identity = await identityApi.getBackstageIdentity();
          eventsApi.publish(
            userSignedInTopic.createEvent(
              {},
              {
                generatorSystem: 'settings',
                originatorUri: identity.userEntityRef,
              },
            ),
            await identityApi.getCredentials(),
          );
          props.onSignInSuccess(identityApi);
        }}
      />
    </>
  );
};

function routes() {
  return (
    <FlatRoutes>
      <Route path="/" element={<WbHomeRedirect />} />
      {/* TODO: check if this page is needed after moved software catalog to admin page */}
      <Route
        path="/catalog"
        element={
          <WbRequirePermission
            when={ctx => ctx.catalogEnabled && ctx.softwareCatalogEnabled}
          >
            <CatalogIndexPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/platform-settings/catalog"
        element={
          <WbRequirePermission
            when={ctx =>
              ctx.platformSettingsEnabled &&
              ctx.softwareCatalogEnabled &&
              ctx.catalogEnabled
            }
          >
            <AdministratorCatalogIndexPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/platform-settings/catalog/:namespace/:kind/:name"
        element={
          <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
            <CatalogEntityPage />
          </WbRequirePermission>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/my-projects/catalog"
        element={
          <WbRequirePermission when={ctx => ctx.catalogEnabled}>
            <MyProjectsPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/my-projects/catalog/:namespace/:kind/:name"
        element={
          <WbRequirePermission when={ctx => ctx.catalogEnabled}>
            <CatalogEntityPage />
          </WbRequirePermission>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/catalog/release/:kind/:namespace/:name"
        element={
          <WbRequirePermission when={ctx => ctx.catalogEnabled}>
            <ReleaseDetailPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/catalog/:namespace/:kind/:name"
        element={
          <WbRequirePermission when={ctx => ctx.catalogEnabled}>
            <CatalogEntityPage />
          </WbRequirePermission>
        }
      >
        {entityPage}
      </Route>
      <Route
        path="/catalog-graph"
        element={
          <WbRequirePermission when={ctx => ctx.catalogEnabled}>
            <CatalogGraphPage
              renderNode={CatalogGraphRenderNode}
              initialState={{
                selectedKinds: ['component', 'system'],
                selectedRelations: [
                  RELATION_HAS_PART,
                  RELATION_PART_OF,
                  RELATION_DEPENDS_ON,
                  RELATION_DEPENDENCY_OF,
                ],
              }}
              relationPairs={WITBOOST_RELATION_PAIRS}
            />
          </WbRequirePermission>
        }
      />
      <Route
        path="/docs/:namespace/:kind/:name/*"
        element={
          <TechDocsReaderPage>
            <TechDocsReaderPageContent />
          </TechDocsReaderPage>
        }
      />
      <Route
        path="/templates"
        element={
          <WbRequirePermission when={ctx => ctx.templatesEnabled}>
            <TemplatesPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/blueprints"
        element={
          <WbRequirePermission when={ctx => ctx.blueprintsEnabled}>
            <BlueprintsPage />
          </WbRequirePermission>
        }
      >
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/platform-settings/catalog-import"
        element={
          <RequirePermission permission={catalogEntityCreatePermission}>
            <CatalogImportPage />
          </RequirePermission>
        }
      />
      <Route path="/settings" element={<UserSettingsPage />}>
        <UserSettingsTab
          path="/advanced"
          title="Advanced"
          requiredModules={getAdvancedRequiredModules()}
        >
          <CommercialGate required={getUserConfigsRequiredModules()}>
            <UserConfigSettings />
          </CommercialGate>
          <CommercialGate required={getUserHeadersRequiredModules()}>
            <UserHeadersSettings />
          </CommercialGate>
          <CommercialGate required={getUserAccessTokensRequiredModules()}>
            <UserAccessTokenSettings />
          </CommercialGate>
        </UserSettingsTab>
        <UserSettingsTab path="documents-table" title="Documents">
          <DocumentsViewerTab />
        </UserSettingsTab>
      </Route>
      <Route
        path="/marketplace"
        element={
          <WbRequirePermission when={ctx => ctx.marketplaceEnabled}>
            <MarketplacePage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/scaffolder"
        element={
          <WbRequirePermission when={ctx => ctx.templatesEnabled}>
            <ScaffolderPage />
          </WbRequirePermission>
        }
      >
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/governance"
        element={
          <WbRequirePermission when={ctx => ctx.governanceEnabled}>
            <GovernancePage />
          </WbRequirePermission>
        }
      />
      <Route path="/notifications" element={<NotificationPage />} />
      <Route
        path="/platform-settings"
        element={
          <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
            <PlatformSettingsPage />
          </WbRequirePermission>
        }
      >
        {/* pass custom layouts so they can be accessed by template studio under
        this route */}
        {scaffolderLayouts()}
      </Route>
      <Route
        path="/platform-settings/rbac"
        element={
          <WbRequirePermission when={ctx => ctx.platformSettingsEnabled}>
            <RbacPanel />
          </WbRequirePermission>
        }
      />

      <Route
        path="/platform-settings/extension-manager"
        element={
          <WbRequirePermission when={ctx => ctx.extensionManagerSectionEnabled}>
            <WbExtensionManagerPage />
          </WbRequirePermission>
        }
      />
      <Route
        path="/prototypes"
        element={
          <WbRequirePermission when={ctx => ctx.prototypePageEnabled}>
            <WbSystemPrototypePage />
          </WbRequirePermission>
        }
      >
        {scaffolderLayouts()}
      </Route>
    </FlatRoutes>
  );
}

const WitboostApp = () => {
  const { error, loading, theme } = useCustomTheme();
  const themes = useMemo(() => {
    if (theme && !loading && !error) return [theme];
    return [LightAgileTheme];
  }, [error, loading, theme]);

  if (loading) return <></>;

  const app = createApp({
    apis,
    plugins: Object.values(plugins),
    themes: themes,
    components: {
      SignInPage: SignInComponent,
    },
    bindRoutes({ bind }) {
      bind(catalogPlugin.externalRoutes, {
        registerComponent: catalogImportPlugin.routes.importPage,
        createComponent: scaffolderPlugin.routes.root,
        viewTechDoc: techdocsPlugin.routes.docRoot,
        templatesRootRouteRef: scaffolderPlugin.routes.templateRouteRef,
        blueprintsRootRouteRef: scaffolderPlugin.routes.blueprintRouteRef,
        templateIndexRouteRef:
          platformSettingsPlugin.routes.templateIndexRouteRef,
        taskListRouteRef: platformSettingsPlugin.routes.taskListRouteRef,
        templateEditorRouteRef:
          platformSettingsPlugin.routes.templateEditorRouteRef,
        actionsRouteRef: platformSettingsPlugin.routes.actionsRouteRef,
        taskRouteRef: platformSettingsPlugin.routes.taskRouteRef,
        iamRouteRef: platformSettingsPlugin.routes.iamRouteRef,
      });
      bind(catalogGraphPlugin.externalRoutes, {
        catalogEntity: catalogPlugin.routes.catalogEntity,
      });
      bind(scaffolderPlugin.externalRoutes, {
        registerComponent: catalogImportPlugin.routes.importPage,
        templateIndexRouteRef:
          platformSettingsPlugin.routes.templateIndexRouteRef,
        actionsRouteRef: platformSettingsPlugin.routes.actionsRouteRef,
        templateEditorRouteRef:
          platformSettingsPlugin.routes.templateEditorRouteRef,
        taskListRouteRef: platformSettingsPlugin.routes.taskListRouteRef,
      });
      bind(catalogImportPlugin.externalRoutes, {
        templates: scaffolderPlugin.routes.root,
        catalog: catalogPlugin.routes.catalogIndex,
        adminCatalogRouteRef: catalogPlugin.routes.adminCatalogRouteRef,
      });
      bind(orgPlugin.externalRoutes, {
        catalogIndex: catalogPlugin.routes.catalogIndex,
      });
      bind(wbExtensionManagerPlugin.externalRoutes, {
        policyPage: governancePlugin.routes.testPolicyPage,
      });
      bind(accessControlPlugin.externalRoutes, {
        marketplaceProductRef: marketplacePlugin.routes.marketplaceProductRef,
      });
    },
  });

  const AppRoot = app.createRoot(
    <>
      <DocumentsPopup />
      <OAuthRequestDialog />
      <AppRouter>
        <CustomAlertDisplay />
        <MeshApolloProvider>
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <AutoLogoutProvider>
                <EnvironmentsContextProvider>
                  <EnabledFeaturesProvider>
                    <Root>{routes()}</Root>
                  </EnabledFeaturesProvider>
                </EnvironmentsContextProvider>
              </AutoLogoutProvider>
            </NotificationProvider>
          </QueryClientProvider>
        </MeshApolloProvider>
      </AppRouter>
    </>,
  );

  return <AppRoot />;
};

export default WitboostApp;
